import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Alert, App, Button, Input, Modal } from 'antd';
import { CalendarOutlined, CheckCircleOutlined, CopyOutlined, StopOutlined } from '@ant-design/icons';
import DOLayout from '../../components/directOrders/DOLayout';
import { StatusBadge } from '../../components/cfq/CfqParts';
import { useAuth } from '../../context/AuthContext';
import cfqService from '../../services/cfqService';
import { openApiFile } from '../../utils/apiAuth';
import { formatDate, formatLong } from '../../utils/workingDays';

const CfqDetail = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const { user } = useAuth();
  const { message } = App.useApp();
  const [request, setRequest] = useState(null);
  const [error, setError] = useState(null);
  const [sop, setSop] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    cfqService.get(id).then(setRequest).catch((err) => setError(err.message));
    cfqService.sop().then(setSop).catch(() => {});
  }, [id]);

  const back = { to: '/cfq', label: 'Call for Quote' };
  if (error || !request) {
    return (
      <DOLayout title="Call for Quote" back={back} showNav={false}>
        {error ? <Alert type="error" showIcon message={error} /> : <p className="do-muted">Loading…</p>}
      </DOLayout>
    );
  }

  const reference = `CFQ${request.year}/${request.number}`;
  const mine = request.requested_by === (user?.userId || user?.id);
  const issued = state?.issued && mine;
  const cancelled = request.status === 'cancelled';

  const copy = () =>
    navigator.clipboard.writeText(reference).then(
      () => message.success(`${reference} copied`),
      () => message.error('Could not copy. Select the reference and copy it instead.')
    );

  const cancel = async () => {
    setSaving(true);
    try {
      setRequest(await cfqService.cancel(request.id, reason.trim() || undefined));
      setCancelling(false);
      message.success(`${reference} cancelled. Administration has been told.`);
    } catch (err) {
      message.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const downloadSop = (e) => {
    e.preventDefault();
    openApiFile(cfqService.sopFileUrl, { download: true, filename: sop?.file_name }).catch((err) => message.error(err.message));
  };

  return (
    <DOLayout title={reference} back={back} showNav={false}>
      <div className="cfq-detail" style={{ marginTop: 24 }}>
        <div className="cfq-detail__main">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {issued && (
              <span className="cfq-issued">
                <CheckCircleOutlined aria-hidden="true" />
                Reference issued
              </span>
            )}
            <p className="cfq-stamp">
              <span>CFQ{request.year}/</span>
              {request.number}
            </p>
            <p style={{ margin: '6px 0 0', fontSize: 16 }}>{request.description}</p>
            <div>
              <StatusBadge status={request.status} />
            </div>
          </div>

          {cancelled ? (
            <div className="cfq-note cfq-note--bad">
              <StopOutlined aria-hidden="true" />
              <span>
                <strong>Cancelled{request.cancelled_at ? ` on ${new Date(request.cancelled_at).toLocaleDateString('en-GB')}` : ''}.</strong>{' '}
                {request.cancel_reason ? `Reason: ${request.cancel_reason}` : 'No reason given.'}
              </span>
            </div>
          ) : (
            request.closing_date && (
              <div className="cfq-note">
                <CalendarOutlined aria-hidden="true" />
                <span>
                  <strong>Valid until the call closes on {formatLong(request.closing_date)}.</strong>
                </span>
              </div>
            )
          )}

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Button type="primary" icon={<CopyOutlined />} onClick={copy}>
              Copy reference
            </Button>
            {mine && !cancelled && (
              <Button danger onClick={() => setCancelling(true)}>
                Cancel this CfQ
              </Button>
            )}
          </div>
          {issued && <p className="do-muted" style={{ margin: 0 }}>We’ve emailed this number to you, and Administration has been told.</p>}

          <dl className="cfq-facts">
            <div><dt>Festival or event</dt><dd>{request.festival || '—'}</dd></div>
            <div><dt>Method</dt><dd>{request.method || '—'}</dd></div>
            <div><dt>Issue date</dt><dd>{formatDate(request.issue_date) || '—'}</dd></div>
            <div><dt>Closing date</dt><dd>{formatDate(request.closing_date) || '—'}</dd></div>
            <div><dt>Requested by</dt><dd>{request.requester_name}</dd></div>
            <div><dt>Requested on</dt><dd>{new Date(request.requested_at).toLocaleDateString('en-GB')}</dd></div>
          </dl>
        </div>

        {mine && !cancelled && (
          <aside className="do-panel">
            <div className="do-panel__head">
              <h2 className="do-panel__title">Next</h2>
            </div>
            <div className="do-panel__body">
              <ol className="cfq-steps">
                <li>
                  <span>
                    Send your call with <strong>{reference}</strong> in the subject.
                  </span>
                </li>
                {sop && (
                  <li>
                    <span>
                      If you need it, attach the SOP for Incoming Invoices/Estimates.{' '}
                      <a href={cfqService.sopFileUrl} onClick={downloadSop}>Download the SOP</a>
                    </span>
                  </li>
                )}
                <li>
                  <span>If the call is cancelled, cancel it here. Administration is told automatically.</span>
                </li>
              </ol>
              <p style={{ margin: '20px 0 0' }}>
                <Link to="/cfq" className="do-link">Back to my CfQs</Link>
              </p>
            </div>
          </aside>
        )}
      </div>

      <Modal
        title={`Cancel ${reference}?`}
        open={cancelling}
        onCancel={() => setCancelling(false)}
        onOk={cancel}
        okText="Cancel this CfQ"
        okButtonProps={{ danger: true, loading: saving }}
        cancelText="Keep it"
      >
        <p>Do this when the services, works or supplies are no longer required. Administration gets an email. The number isn’t reused.</p>
        <label htmlFor="cfq-cancel-reason" style={{ display: 'block', fontWeight: 500, marginBottom: 6 }}>
          Reason <span className="do-muted" style={{ fontWeight: 400 }}>(optional)</span>
        </label>
        <Input.TextArea id="cfq-cancel-reason" rows={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Modal>
    </DOLayout>
  );
};

export default CfqDetail;
